import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { UserRole } from '@/types/chat';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';

interface User {
  id: string;
  email: string;
  role: UserRole;
  name: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  accessDenied: boolean;
  clearAccessDenied: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabaseAdmin.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        if (event === 'SIGNED_OUT') {
          // Open realtime subscriptions would otherwise keep running as anon
          supabaseAdmin.removeAllChannels();
        }
        if (session?.user) {
          // Fetch user profile and role
          setTimeout(() => {
            fetchUserData(session.user);
          }, 0);
        } else {
          setUser(null);
        }
      }
    );

    // Check for existing session
    supabaseAdmin.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        fetchUserData(session.user);
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserData = async (supabaseUser: SupabaseUser) => {
    const db = supabaseAdmin as any;
    try {
      // Try to fetch from profiles table first, then fallback to users table
      let userName = '';

      // First try profiles table
      const { data: profile, error: profileError } = await db
        .from('profiles')
        .select('name, full_name')
        .eq('id', supabaseUser.id)
        .maybeSingle();

      if (profile) {
        userName = profile.name || profile.full_name || '';
      } else {
        // Fallback to users table if profiles doesn't have entry
        console.log('[useAdminAuth] No profile found, trying users table');
        const { data: userRecord } = await db
          .from('users')
          .select('full_name')
          .eq('id', supabaseUser.id)
          .maybeSingle();

        if (userRecord) {
          userName = userRecord.full_name || '';
        } else {
          console.warn('[useAdminAuth] No user record found in profiles or users table');
        }
      }

      // Fetch role from user_roles table
      const { data: roleData, error: roleError } = await db
        .from('user_roles')
        .select('role')
        .eq('user_id', supabaseUser.id)
        .maybeSingle();

      if (roleError) {
        console.error('[useAdminAuth] Error fetching role:', roleError);
      }

      const VALID_STAFF_ROLES = ['admin', 'billing', 'editing', 'sales', 'talent_coordinator'];
      const rawRole = roleData?.role as string | null;

      // If no role found or role is not a valid staff role, deny access entirely
      if (!rawRole || !VALID_STAFF_ROLES.includes(rawRole.toLowerCase())) {
        await supabaseAdmin.auth.signOut();
        setUser(null);
        setAccessDenied(true);
        return;
      }

      const normalizedRole = rawRole.toLowerCase().replace(/\s+/g, '_') as UserRole;

      setUser({
        id: supabaseUser.id,
        email: supabaseUser.email!,
        name: userName || supabaseUser.email?.split('@')[0] || '',
        role: normalizedRole,
      });
    } catch (error) {
      console.error('[useAdminAuth] Error fetching user data:', error);
      // On error, deny access — never grant admin by default
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const { error } = await supabaseAdmin.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Login error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    await supabaseAdmin.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const clearAccessDenied = () => setAccessDenied(false);

  return (
    <AuthContext.Provider 
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        isLoading,
        accessDenied,
        clearAccessDenied,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
